import { QueryClient } from '@tanstack/react-query';
import { CollaborationInstance, MessageItem } from '../models/LeftPanel';
import { MESSAGES_QUERY_KEY } from '../hooks/useConversationMessages';

// The default topic the backend assigns to a freshly created, still-empty chat.
export const EMPTY_INSTANCE_TOPIC = 'New chat';

// A chat can still be titled "New chat" while already holding messages (the
// topic is only auto-renamed later). Check the React Query message cache so we
// don't send the user back into a chat that already has a conversation.
export const instanceHasCachedMessages = (
  queryClient: QueryClient,
  instanceId: string
): boolean => {
  const data = queryClient.getQueryData<{ pages?: Array<{ messages?: unknown[] }> }>(
    MESSAGES_QUERY_KEY(instanceId)
  );
  return (data?.pages || []).some(page => (page.messages?.length ?? 0) > 0);
};

// Only reuse the newest chat: the first active instance in the list (new
// instances are prepended). Reuse it only if it's still the default
// "New chat" AND has no messages cached for it. Older chats are ignored.
// Shared by the "+ New Chat" button and the login auto-create flow so both
// apply the exact same "don't stack up empty New chats" rule.
export const findReusableNewChatInstance = (
  queryClient: QueryClient,
  item: MessageItem
): CollaborationInstance | undefined => {
  const firstActive = (item.collab_instances || []).find(inst => inst.status === 'active');
  if (
    firstActive &&
    (firstActive.topic || '').trim().toLowerCase() === EMPTY_INSTANCE_TOPIC.toLowerCase() &&
    !instanceHasCachedMessages(queryClient, firstActive.id)
  ) {
    return firstActive;
  }
  return undefined;
};
